import React from "react";
import { Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { CalculatorIcon } from "@sx-icons";
import { type UnitConverterType } from "@common/components/ui-components/SXEditField";
import { useAppSelector } from "../app/hooks";
import { selectHasAnalysisOutput } from "../features/projects/projectSlice";
import { selectCriticalLoadExceeded } from "../features/ui/uiSlice";

// Pieces the concrete and steel/timber design settings share. They differ in what the user gets to
// choose, not in how utilization is entered or how the run is started.

// limit_utilization is stored as a ratio (0-1) but presented to the user as a percentage.
// SXEditField applies toInput for display and fromInput before invoking onUpdate, so the
// panel state and the saved file always stay in ratio form.
export const RATIO_TO_PERCENT_UNIT: UnitConverterType = {
  toInput: (ratio: number) => ratio * 100,
  fromInput: (percent: number) => percent / 100,
  getDisplayString: () => "%",
};

interface DesignRunButtonProps {
  isDesigning: boolean;
  onRun: () => void;
  /**
   * Blocks the run for a reason the user can act on (nothing selected, too many candidates). Shown
   * above the button so the disabled state is never unexplained.
   */
  blockedReason?: string | null;
}

/** The button that starts a design run, with its in-flight state. */
export const DesignRunButton = ({ isDesigning, onRun, blockedReason }: DesignRunButtonProps) => {
  const { t } = useTranslation();
  // The design candidates are compared against the section forces of the current analysis, so a
  // run needs one — and every input change prunes the output, which is why the design button goes
  // dead together with the code check button rather than only on the first visit to a project.
  // Enforced here, on the piece all three materials share, so the state cannot drift per material.
  const hasAnalysisOutput = useAppSelector(selectHasAnalysisOutput);
  // An analysis that hit the critical load counts as no analysis: its section forces are
  // meaningless, so a design run on them would be too (App.tsx raises the matching alert and drops
  // the results). Gated here as well, so the code check and the design open up together again once
  // changed input produces usable section forces.
  const criticalLoadExceeded = useAppSelector(selectCriticalLoadExceeded);
  // The missing analysis outranks the settings-level blockers: nothing the user picks here can
  // start a run until it has been carried out.
  const reason = criticalLoadExceeded
    ? t("critical_load_exceeded_.label", "Critical load exceeded")
    : hasAnalysisOutput
      ? blockedReason
      : t("design_needs_analysis_.label", "An analysis needs to be run before a design can be started.");
  return (
    <>
      {reason && <div className="sx-cd-blocked">{reason}</div>}
      <Button
        id="project_result_run_design"
        className="primary-btn w-100 ms-0 mx-0"
        title="design"
        disabled={isDesigning || !!reason}
        onClick={onRun}
      >
        {isDesigning ? (
          <>
            <Spinner as="span" size="sm" animation="border" role="status" aria-hidden="true" className="me-2" />
            <span>{t("designing_.label", "Designing…")}</span>
          </>
        ) : (
          <>
            <span>{t("design_.label")}</span> <CalculatorIcon size={14} />
          </>
        )}
      </Button>
    </>
  );
};
